import { SurveyBundle } from '../survey-bundle/survey-bundle.class';
import { SurveySession } from '../survey-session/survey-session.class';
import {
  SurveyListInstruction,
  SurveyListOption,
} from '@hyper-inferno/survey-elements-core';
import {
  ITransformationContext,
  QuestionHandler,
} from '../survey-engine/survey-engine.class';
import { shuffle, uniqBy } from 'lodash';
import { CustomScriptEngine } from './custom-script.engine';

export class ListInstructionProcessor {
  /**
   * Main entry point to transform instructions into a final list of options.
   */
  public static process(
    listId: string,
    ctx: ITransformationContext,
    partialData = {},
  ): SurveyListOption[] {
    const { bundle, session, handlers } = ctx;
    const listDef = bundle.getListByListIdQuery(listId);
    if (!listDef) return [];

    const instructions = listDef.instructions || [];
    let currentOptions: SurveyListOption[] = [];

    // Process each instruction in sequence
    session.jsn_responses = {
      ...session.jsn_responses,
      ...partialData,
    };
    try {
      for (const inst of instructions) {
        currentOptions = this.executeInstruction(
          inst,
          currentOptions,
          bundle,
          session,
          handlers,
          ctx,
        );
      }
    } catch (e) {
      throw Error(e as any);
    }

    // Apply global randomization if requested and not already handled by a specific instruction
    // if (
    //   randomizeRequested &&
    //   !instructions.some((i) => i.function === 'RANDOMIZE')
    // ) {
    //   currentOptions = shuffle(currentOptions);
    // }

    return currentOptions;
  }

  private static executeInstruction(
    inst: SurveyListInstruction,
    currentOptions: SurveyListOption[],
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    // SurveyListInstruction interface does not currently
    // expose these dynamic properties.
    const instruction = inst as any;

    const func = instruction.function;
    const targets: string[] =
      instruction.option_targets || [];

    switch (func) {
      // ADD
      case 'ADD': {
        const sourceItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );

        const position = this.resolvePosition(
          instruction,
          currentOptions.length,
        );

        const newList = [...currentOptions];

        newList.splice(
          position,
          0,
          ...sourceItems,
        );

        return uniqBy(newList, 'id');
      }

      // AIC - Add If Chosen
      case 'AIC': {
        const sourceItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );

        const position = this.resolvePosition(
          instruction,
          currentOptions.length,
        );

        const newList = [...currentOptions];

        newList.splice(
          position,
          0,
          ...sourceItems,
        );

        return uniqBy(newList, 'id');
      }

      // ANC - Add If Not Chosen
      case 'ANC': {
        const sourceItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );

        const position = this.resolvePosition(
          instruction,
          currentOptions.length,
        );

        const newList = [...currentOptions];

        newList.splice(
          position,
          0,
          ...sourceItems,
        );

        return uniqBy(newList, 'id');
      }

      // REMOVE
      case 'REMOVE': {
        const sourceItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );

        if (sourceItems.length === 0) {
          return currentOptions;
        }

        const removeIds = new Set(
          sourceItems.map(
            (option) => option.id,
          ),
        );

        return currentOptions.filter(
          (option) => !removeIds.has(option.id),
        );
      }

      // RIC - Remove If Chosen
      case 'RIC': {
        const chosenItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );

        if (chosenItems.length === 0) {
          return currentOptions;
        }

        const removeIds = new Set(
          chosenItems.map(
            (option) => option.id,
          ),
        );

        return currentOptions.filter(
          (option) => !removeIds.has(option.id),
        );
      }

      // RANDOMIZE
      case 'RANDOMIZE': {
        const specifyRange =
          instruction.parameter_1 === 'true';

        if (!specifyRange) {
          return shuffle(currentOptions);
        }

        const start = Math.max(
          0,
          parseInt(
            instruction.parameter_2 || '1',
          ) - 1,
        );

        const end = Math.min(
          currentOptions.length,
          parseInt(
            instruction.parameter_3 ||
            String(currentOptions.length),
          ),
        );

        const prefix =
          currentOptions.slice(0, start);

        const mid =
          shuffle(
            currentOptions.slice(start, end),
          );

        const suffix =
          currentOptions.slice(end);

        return [
          ...prefix,
          ...mid,
          ...suffix,
        ];
      }

      // LIST LENGTH
      case 'LIST_LENGTH': {
        // Your interface uses parameter_1 etc.
        const limit = parseInt(
          instruction.parameter_1 || '0',
        );

        const limitType =
          instruction.type;

        if (isNaN(limit)) {
          return currentOptions;
        }

        switch (limitType) {
          case 'atmost':
            // Truncate to limit if length exceeds it
            return currentOptions.slice(
              0,
              limit,
            );

          case 'exactly':
            // Force the list to be exactly this length (truncate if too many,
            // though usually 'exactly' implies validation elsewhere)
            return currentOptions.slice(
              0,
              limit,
            );

          case 'atleast':
            // 'atleast' does not truncate the list.
            // It serves as a marker for validation logic later.
            return currentOptions;

          default:
            return currentOptions;
        }
      }

      // CUSTOM SCRIPT
      case 'CUSTOM_SCRIPT': {
        const code =
          instruction.parameters?.[0] ??
          instruction.parameter_1 ??
          '';
        const source =
          this.getSourceItems(
            inst,
            bundle,
            session,
            handlers,
            targets,
            ctx,
          );

        const result =
          CustomScriptEngine.execute(
            code,
            currentOptions.map(
              (option) =>
                option.option_key,
            ),
            {
              bundle,
              session,
              handlers,
              ctx,
            },
          );

        return result
          .map((key: number) =>
            source.find(
              (option) =>
                option.option_key === key,
            ),
          )
          .filter(
            (
              option,
            ): option is SurveyListOption =>
              !!option,
          );
      }

      default:
        return currentOptions;
    }
  }
  static getAnswer(
    function_question_id: number,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
  ): string[] {
    const element = bundle.fetchElementById(function_question_id);
    if (!element) {
      return [];
    }

    // Get the handler for that question type
    // (Single Choice, Numeric, etc.)
    const handler = handlers[element.type];

    if (
      !handler ||
      typeof handler.getValue !== 'function'
    ) {
      console.warn(
        `No getValue implementation for ${element.type}`,
      );
      return [];
    }

    const elementAnswerObject =
      session.jsn_responses[element.id] ?? {};

    if (Object.keys(elementAnswerObject).length === 0) {
      throw new Error('AF Mode might be needed');
    }

    // Get user's answer via the specific handler
    const userValue: number[] = handler.getValue(
      element,
      elementAnswerObject,
    ) as number[];

    const mainList = bundle.getListOfQuestion(element.id);

    const selectedOptions = mainList?.options
      .filter((option) =>
        userValue.includes(option.option_key),
      )
      .map((option) => option.id);

    return selectedOptions ?? [];
  }

  /**
   * Determines WHERE to insert items
   * (parameter_1, parameter_2, parameter_3)
   */
  private static resolvePosition(
    inst: any,
    currentLength: number,
  ): number {
    const specifyPos =
      inst.parameter_1 === 'true';

    if (!specifyPos) {
      return currentLength;
    }

    const posType = inst.parameter_2;

    if (posType === 'top') {
      return 0;
    }

    if (posType === 'bottom') {
      return currentLength;
    }

    if (posType === 'custom position') {
      const custom =
        parseInt(inst.parameter_3) - 1;

      return Math.max(
        0,
        Math.min(currentLength, custom),
      );
    }

    return currentLength;
  }

  // Refactor:
  private static getSourceItems(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    targets: string[],
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const baseSet = this.resolveBaseSet(
      inst,
      bundle,
      session,
      handlers,
      targets,
      ctx,
    );

    return this.applySubSelection(
      baseSet,
      inst,
    );
  }

  // --- Dispatcher -----------------------------------------------------------

  private static resolveBaseSet(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    targets: string[],
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    switch (inst.function) {
      case 'ADD':
        return this.getSourceItemsForAdd(
          inst,
          targets,
          ctx,
        );

      case 'REMOVE':
        return this.getSourceItemsForRemove(
          inst,
          targets,
          ctx,
        );

      case 'CUSTOM_SCRIPT':
        return this.getSourceItemsForCustomScript(
          inst,
          ctx,
        );

      case 'AIC':
        return this.getSourceItemsForAic(
          inst,
          bundle,
          session,
          handlers,
          ctx,
        );

      case 'ANC':
        return this.getSourceItemsForAnc(
          inst,
          bundle,
          session,
          handlers,
          ctx,
        );

      case 'RIC':
        return this.getSourceItemsForRic(
          inst,
          bundle,
          session,
          handlers,
          ctx,
        );

      default:
        return [];
    }
  }

  // --- List-based functions
  // ADD / REMOVE / CUSTOM_SCRIPT -------------------

  private static getFunctionListId(
    inst: SurveyListInstruction,
  ): string {
    const targetListId =
      inst.function_list_id ||
      inst.instruction_set_list_id;

    if (!targetListId) {
      throw new Error(
        'Error Identifying source list: No function list id',
      );
    }

    return targetListId;
  }

  private static getSourceItemsForAdd(
    inst: SurveyListInstruction,
    targets: string[],
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const listId =
      this.getFunctionListId(inst);

    const baseSet =
      this.getCachedListOptions(
        listId,
        ctx,
      );

    if (targets.length > 0) {
      return baseSet.filter((opt) =>
        targets.includes(opt.id),
      );
    }

    return baseSet;
  }

  private static getSourceItemsForRemove(
    inst: SurveyListInstruction,
    targets: string[],
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const listId =
      this.getFunctionListId(inst);

    const baseSet =
      this.getCachedListOptions(
        listId,
        ctx,
      );

    if (targets.length > 0) {
      return baseSet.filter(
        (opt) => !targets.includes(opt.id),
      );
    }

    return baseSet;
  }

  private static getSourceItemsForCustomScript(
    inst: SurveyListInstruction,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const listId =
      this.getFunctionListId(inst);

    // No ADD/REMOVE-style target filtering
    // applies to CUSTOM_SCRIPT.
    return this.getCachedListOptions(
      listId,
      ctx,
    );
  }

  private static getFunctionQuestionId(
    inst: SurveyListInstruction,
  ): number {
    if (!inst.function_question_id) {
      throw new Error(
        'Error Identifying source list: function_question_id',
      );
    }

    return inst.function_question_id;
  }

  private static getChosenIds(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
  ): string[] {
    const questionId =
      this.getFunctionQuestionId(inst);

    try {
      return (
        this.getAnswer(
          questionId,
          bundle,
          session,
          handlers,
        ) || []
      );
    } catch (error) {
      throw new Error(error as any);
    }
  }

  private static getAnswerFunctionBaseSet(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const questionId =
      this.getFunctionQuestionId(inst);

    const element =
      bundle.fetchElementById(questionId);

    const listId =
      element?.config?.['list_id'] ||
      (element as any)?.list_id;

    if (listId) {
      return this.getCachedListOptions(
        listId,
        ctx,
      );
    }

    // Fallback to reading raw structural payload
    // schema if properties layout aren't found
    const sourceList =
      bundle.getListOfQuestion(questionId);

    return sourceList?.options || [];
  }

  private static getSourceItemsForAic(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const chosenIds =
      this.getChosenIds(
        inst,
        bundle,
        session,
        handlers,
      );

    const baseSet =
      this.getAnswerFunctionBaseSet(
        inst,
        bundle,
        ctx,
      );

    return baseSet.filter((opt) =>
      chosenIds.includes(opt.id),
    );
  }

  private static getSourceItemsForAnc(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const chosenIds =
      this.getChosenIds(
        inst,
        bundle,
        session,
        handlers,
      );

    const baseSet =
      this.getAnswerFunctionBaseSet(
        inst,
        bundle,
        ctx,
      );

    return baseSet.filter(
      (opt) => !chosenIds.includes(opt.id),
    );
  }

  private static getSourceItemsForRic(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const chosenIds =
      this.getChosenIds(
        inst,
        bundle,
        session,
        handlers,
      );

    const baseSet =
      this.getAnswerFunctionBaseSet(
        inst,
        bundle,
        ctx,
      );

    // RIC = Remove If Chosen
    // Return the chosen items.
    // executeInstruction() removes them
    // from currentOptions.
    return baseSet.filter(
      (opt) => chosenIds.includes(opt.id),
    );
  }

  // --- Shared cache helper -----------------------------------------------

  private static getCachedListOptions(
    listId: string,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    if (!(ctx as any).listCache) {
      (ctx as any).listCache = {};
    }

    const cache = (ctx as any).listCache;

    if (cache[listId] === '__COMPUTING__') {
      throw new Error(
        `Circular dependency detected processing list ID: ${listId}`,
      );
    }

    if (!cache[listId]) {
      cache[listId] = '__COMPUTING__';

      cache[listId] =
        ctx.listBuilding.getOptionsByListId(
          listId,
          ctx,
          false,
        );
    }

    return cache[listId];
  }

  // --- Sub-selection ("type" logic) ---------------------------------------

  private static applySubSelection(
    baseSet: SurveyListOption[],
    inst: SurveyListInstruction,
  ): SurveyListOption[] {
    const selectionType = inst.type;

    if (selectionType === 'single item') {
      const idx =
        parseInt(inst.parameter_4!) - 1;

      return baseSet[idx]
        ? [baseSet[idx]]
        : [];
    }

    if (selectionType === 'range') {
      const start =
        parseInt(inst.parameter_5!) - 1;

      const end =
        parseInt(inst.parameter_6!);

      return baseSet.slice(
        start,
        end,
      );
    }

    return baseSet;
  }
}